import { useTranslation } from 'react-i18next'

import { type Charge } from '../../modules/invoices'
import { useQuoteItemValueFormatter, useShowDescription } from '../../modules/quotes'
import { capitalizeFirstLetter } from '../../utils/string'
import Price from '../Price'
import InfoTooltip from '../InfoTooltip'

type ChargeRowProps = {
  charge: Charge
}

const ChargeRow: React.FC<ChargeRowProps> = ({ charge }) => {
  const { t } = useTranslation()
  const getItemValue = useQuoteItemValueFormatter()

  const item = getItemValue(charge.description, charge.item)
  const showDescription = useShowDescription(charge.description)
  const description = charge.overrideDescription ?? t(`quotes.pricing.description.${charge.description}`)
  const tooltip = t(`quotes.pricing.tooltip.${charge.description}`, { defaultValue: '' })

  return (
    <tr className="border-b border-dashed align-bottom sm:align-middle">
      <td className="w-1/2 sm:w-auto">
        <div className="mb-1 text-sm text-gray-500 sm:mb-0">
          { showDescription
            ? (
              <>
                { description }
                { item && <> ({ capitalizeFirstLetter(item) })</> }
                { tooltip && ' ' }
                { tooltip && <InfoTooltip tooltip={tooltip} className="!text-[20px]" /> }
              </>
              )

            : capitalizeFirstLetter(item) }
        </div>
      </td>
      <td>
        <div className="hidden whitespace-nowrap text-right text-sm text-gray-600 sm:block">
          { charge.quantity }
          { charge.unit && t(`units.${charge.unit}`) }
        </div>
      </td>
      <td>
        <div className="hidden whitespace-nowrap text-right text-sm text-gray-600 sm:block">
          <Price amount={charge.unitPrice} compact />
        </div>
      </td>
      <td className="w-1/2 sm:w-auto">
        <div className="text-right text-gray-500">
          <div className="my-1 -mr-2 inline-flex flex-wrap justify-end gap-1 rounded bg-gray-200 px-2 py-px text-xs sm:hidden">
            <div>
              { charge.quantity }
              { charge.unit && t(`units.${charge.unit}`) } @
            </div>
            <div className="whitespace-nowrap">
              <Price amount={charge.unitPrice} compact />
            </div>
          </div>
          <div className="mb-1 whitespace-nowrap font-semibold sm:mb-0 sm:font-normal">
            <Price amount={charge.subtotal} compact />
          </div>
        </div>
      </td>
    </tr>
  )
}

export default ChargeRow
